<script setup>
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import UiMenu from "./ui/UiMenu.vue";
import { apiFetch } from "../lib/api";
import {
  clearCurrentUser,
  getCachedCurrentUser,
  getCurrentUser,
} from "../lib/current-user";

const user = ref(getCachedCurrentUser());
const open = ref(false);
const loggingOut = ref(false);
const router = useRouter();

const initials = computed(() => {
  if (!user.value) return "?";
  if (user.value.givenName || user.value.familyName) {
    return `${user.value.givenName?.[0] || ""}${user.value.familyName?.[0] || ""}`.toUpperCase();
  }
  return (user.value.email || "?")[0].toUpperCase();
});

const displayName = computed(() => user.value?.name || user.value?.email || "");
const hasPaidPlan = computed(() =>
  ["pro", "planner", "complete"].includes(user.value?.subscription?.plan),
);

async function handleLogout() {
  loggingOut.value = true;
  try {
    await apiFetch("/api/auth/logout", {
      method: "POST",
      headers: { "X-Requested-With": "PermitPilot" },
    });
  } catch {}
  clearCurrentUser();
  router.push("/login");
}

onMounted(async () => {
  if (!user.value) {
    try {
      user.value = await getCurrentUser();
    } catch {}
  }
});
</script>

<template>
  <UiMenu
    v-if="user"
    v-model="open"
    class="relative flex-shrink-0 w-[72px] h-11 flex items-center justify-end"
    panel-id="user-menu-panel"
    trigger-label="User menu"
    panel-label="Account navigation"
    panel-tag="nav"
    trigger-class="flex min-h-11 items-center gap-2 rounded-lg px-2.5 py-2 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]"
    panel-class="absolute top-full right-0 mt-2 w-56 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] shadow-xl overflow-hidden z-50"
  >
    <template #trigger>
      <span
        class="w-8 h-8 rounded-full bg-[var(--accent-primary)] text-white text-sm font-semibold flex items-center justify-center"
      >
        {{ initials }}
      </span>
      <svg
        aria-hidden="true"
        focusable="false"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        class="text-white/50"
      >
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          fill="none"
        />
      </svg>
    </template>

    <template #panel="{ close }">
      <div class="px-3 py-2.5 border-b border-[var(--color-border)]">
        <div class="text-sm text-white truncate">{{ displayName }}</div>
        <div v-if="user.name" class="text-xs text-white/50 truncate">
          {{ user.email }}
        </div>
        <div
          v-if="user.isAdmin"
          class="text-xs uppercase tracking-wide text-[var(--accent-primary-text)] font-semibold mt-0.5"
        >
          System Admin
        </div>
        <div
          v-else-if="user.isBeta"
          class="text-xs uppercase tracking-wide text-[#22c55e] font-semibold mt-0.5"
        >
          Beta Tester
        </div>
        <div
          v-if="
            ['pro', 'planner', 'complete'].includes(user.subscription?.plan) &&
            ['active', 'trialing'].includes(user.subscription?.status)
          "
          class="text-xs uppercase tracking-wide text-[var(--color-primary-light)] font-semibold mt-0.5"
        >
          {{ user.subscription.status === "trialing" ? "Pro · Trial" : "Pro" }}
        </div>
      </div>
      <RouterLink
        to="/account"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        Account
      </RouterLink>
      <RouterLink
        to="/dashboard?new=1"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        Process Permit
      </RouterLink>
      <RouterLink
        to="/planner"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        Route Planner
      </RouterLink>
      <RouterLink
        v-if="
          user.isAdmin ||
          user.isBeta ||
          ['pro', 'planner', 'complete'].includes(user.subscription?.plan)
        "
        to="/routes"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        Saved Routes
      </RouterLink>
      <RouterLink
        :to="user.isBeta ? '/account' : '/pricing'"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        {{
          user.isBeta
            ? "Beta Access"
            : hasPaidPlan
              ? "Manage Plan"
              : "Upgrade Plan"
        }}
      </RouterLink>
      <RouterLink
        v-if="user.isAdmin"
        to="/admin"
        role="menuitem"
        class="block min-h-11 px-3 py-3 text-sm text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        @click="close()"
      >
        Administration
      </RouterLink>
      <button
        role="menuitem"
        class="min-h-11 w-full border-t border-[var(--color-border)] px-3 py-3 text-left text-sm text-red-400 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]"
        :disabled="loggingOut"
        @click="handleLogout"
      >
        {{ loggingOut ? "Signing out..." : "Sign out" }}
      </button>
    </template>
  </UiMenu>
  <div v-else aria-hidden="true" class="w-[72px] h-11"></div>
</template>
